import React, { useEffect, useRef, useState } from 'react';
import './Privacy.css';

const TRUST_BADGES = [
  { id: 'monitoring', icon: 'radar', label: 'Monitoreo de infraestructura', value: '24/7' },
  { id: 'arco', icon: 'clock', label: 'Respuesta a solicitudes ARCO', value: '72 h' },
  { id: 'data-sale', icon: 'shield', label: 'Venta de datos a terceros', value: '0%' },
];

const DATA_TABLE = [
  {
    type: 'Identidad y contacto',
    purpose: 'Crear cuentas verificadas, emitir contratos y facilitar comunicación segura entre equipos.',
  },
  {
    type: 'Documentación operativa',
    purpose: 'Cumplir requisitos regulatorios (cartas porte, licencias, evidencia de cumplimiento y auditorías).',
  },
  {
    type: 'Datos de uso',
    purpose: 'Optimizar la experiencia, generar analíticos agregados y detectar patrones inusuales.',
  },
  {
    type: 'Seguridad y dispositivos',
    purpose: 'Prevenir fraude y proteger accesos mediante análisis de IP, dispositivos y registros antifraude.',
  },
];

const USAGE_LIST = [
  'Proveer acceso a los módulos contratados personalizando permisos por rol y compañía.',
  'Brindar soporte funcional y operativo, incluyendo recordatorios de vencimientos y acompañamiento en auditorías.',
  'Cumplir obligaciones legales, fiscales y regulatorias aplicables al sector logístico en México.',
  'Mejorar y entrenar nuestros modelos operativos de forma agregada y anonimizando la información cuando corresponde.',
];

const RETENTION_LIST = [
  'Conservamos documentación fiscal y contractual hasta por 10 años o el periodo que marque la normativa vigente.',
  'Eliminamos registros de actividad inactivos después de 24 meses salvo que exista una obligación contractual o legal para preservarlos.',
  'Transferimos datos únicamente a encargados que suscriben cláusulas de confidencialidad, privacidad y medidas de seguridad equivalentes.',
];

const ARCO_CALLOUT = {
  variant: 'arco',
  title: 'Derechos ARCO',
  description: 'Puedes ejercer tus derechos de Acceso, Rectificación, Cancelación y Oposición mediante:',
  list: [
    'El envío de un correo a privacy@flotte.mx detallando la solicitud y acreditando tu identidad.',
    'La recepción de un folio y respuesta en un plazo máximo de 72 horas hábiles.',
    'Formatos descargables para seguimiento y evidencia cuando lo solicites.',
  ],
  footnote:
    'Si actúas en representación de otra persona, adjunta carta poder simple, copia de identificación y cualquier documento que respalde el mandato.',
};

const SECURITY_CONTROLS = [
  'Cifrado con TLS 1.2+ en tránsito y AES-256 en reposo, con rotación programada de llaves.',
  'Monitoreo continuo y alertas en tiempo real respaldadas por un Centro de Operaciones de Seguridad (SOC).',
  'Gestión de accesos internos bajo privilegio mínimo, autenticación multifactor y registros detallados.',
  'Pruebas periódicas de penetración, políticas de clasificación de datos y acuerdos de confidencialidad para personal y proveedores.',
];

const PRIVACY_SECTIONS = [
  {
    id: 'datos',
    label: 'Datos que recopilamos',
    kicker: 'Recolección',
    title: 'Datos que recopilamos',
    paragraphs: [
      'Los datos personales que solicitamos se limitan a lo necesario para operar la plataforma, validar a los participantes y cumplir obligaciones regulatorias del sector transporte.',
    ],
    table: DATA_TABLE,
  },
  {
    id: 'uso',
    label: 'Uso de la información',
    kicker: 'Finalidades',
    title: 'Cómo utilizamos la información',
    paragraphs: [
      'Usamos tus datos para ejecutar el servicio contratado con altos estándares de seguridad y transparencia. Cada finalidad se encuentra ligada a una obligación operativa o legal.',
    ],
    list: USAGE_LIST,
  },
  {
    id: 'conservacion',
    label: 'Conservación y transferencias',
    kicker: 'Custodia',
    title: 'Conservación y transferencias',
    paragraphs: [
      'Definimos plazos de resguardo en función de los requisitos fiscales, regulatorios y contractuales que aplican a cada cliente.',
    ],
    list: RETENTION_LIST,
  },
  {
    id: 'derechos',
    label: 'Derechos ARCO',
    kicker: 'Control personal',
    title: 'Ejercicio de derechos ARCO',
    paragraphs: [
      'Te ofrecemos un canal directo con nuestro Oficial de Protección de Datos para ejercer tus derechos de Acceso, Rectificación, Cancelación y Oposición.',
    ],
    callout: ARCO_CALLOUT,
  },
  {
    id: 'seguridad',
    label: 'Medidas de seguridad',
    kicker: 'Protección activa',
    title: 'Medidas de seguridad y monitoreo',
    paragraphs: [
      'Aplicamos controles técnicos y organizacionales que se revisan de forma continua para mantener la integridad, disponibilidad y confidencialidad de la información.',
    ],
    list: SECURITY_CONTROLS,
  },
];

const Icon = ({ name }) => {
  switch (name) {
    case 'radar':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 3v3m6 6h3m-9 9v-3m-9-6H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          <path d="M12 12l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'clock':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'shield':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 3l7 3v5c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-3z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
};

export default function Privacy() {
  const sectionRefs = useRef({});
  const [activeSection, setActiveSection] = useState(PRIVACY_SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: [0.25, 0.5, 0.75] }
    );

    Object.values(sectionRefs.current).forEach(node => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  const registerSection = id => node => {
    if (node) {
      sectionRefs.current[id] = node;
    }
  };

  return (
    <main className="privacy-doc">
      <section className="privacy-doc__hero">
        <div className="privacy-doc__hero-inner">
          <p className="privacy-doc__eyebrow">Centro de confianza</p>
          <h1 className="privacy-doc__title">Política de Privacidad</h1>
          <p className="privacy-doc__lede">
            En Flotte tratamos los datos con transparencia, minimización y controles técnicos de primer nivel. Esta
            política describe qué recopilamos, cómo lo usamos y los mecanismos para ejercer tus derechos.
          </p>
          <ul className="privacy-trust">
            {TRUST_BADGES.map(badge => (
              <li key={badge.id}>
                <span className="privacy-trust__icon" aria-hidden="true">
                  <Icon name={badge.icon} />
                </span>
                <div>
                  <span className="privacy-trust__value">{badge.value}</span>
                  <p>{badge.label}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="privacy-doc__layout">
        <nav className="privacy-doc__toc" aria-label="Tabla de contenidos">
          <p className="privacy-doc__toc-label">Contenido</p>
          <ul>
            {PRIVACY_SECTIONS.map(section => (
              <li key={section.id}>
                <a href={`#${section.id}`} className={activeSection === section.id ? 'is-active' : undefined}>
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="privacy-doc__content">
          {PRIVACY_SECTIONS.map(section => (
            <section
              key={section.id}
              id={section.id}
              ref={registerSection(section.id)}
              className="privacy-doc__section"
            >
              <div className="privacy-doc__section-head">
                <span className="privacy-doc__section-kicker">{section.kicker}</span>
                <h2>{section.title}</h2>
              </div>

              {section.paragraphs?.map(paragraph => (
                <p key={paragraph}>{paragraph}</p>
              ))}

              {section.table && (
                <div className="privacy-doc__table">
                  <table>
                    <thead>
                      <tr>
                        <th>Tipo de dato</th>
                        <th>Finalidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.map(row => (
                        <tr key={row.type}>
                          <td>{row.type}</td>
                          <td>{row.purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {section.list && (
                <ul className="privacy-doc__list">
                  {section.list.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}

              {section.callout && (
                <aside
                  className={`privacy-doc__callout ${
                    section.callout.variant === 'arco' ? 'privacy-doc__callout--arco' : ''
                  }`}
                >
                  <h3>{section.callout.title}</h3>
                  <p>{section.callout.description}</p>
                  <ul>
                    {section.callout.list.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <p className="privacy-doc__callout-footnote">{section.callout.footnote}</p>
                </aside>
              )}
            </section>
          ))}
        </div>
      </div>

      <footer className="privacy-doc__footer">
        <div className="privacy-doc__dpo">
          <span className="privacy-doc__dpo-icon" aria-hidden="true">
            <Icon name="shield" />
          </span>
          <div>
            <p>Oficial de Protección de Datos</p>
            <a href="mailto:privacy@flotte.mx">privacy@flotte.mx</a>
          </div>
        </div>
      </footer>
    </main>
  );
}
